import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  uploadBinderMedia,
  useCreateBinderEntry,
  useDeleteBinderMedia,
  useUpdateBinderEntry,
  type BinderDateValue,
  type BinderEntry,
  type BinderFact,
  type BinderMedia,
  type BinderTemplateId,
  type CreateBinderEntryInput,
  type DatePrecision,
  type FactValueType,
} from "./binder-api.js";
import {
  BINDER_DATE_KINDS,
  BINDER_TEMPLATES,
  dateKindLabel,
  findTemplate,
} from "./binder-templates.js";
import { BinderMediaThumb } from "./BinderMediaThumb.js";
import { useDraftImages, type DraftImage } from "../rfqs/steps/ImagesStep.js";

const inputClass =
  "block w-full min-h-[44px] rounded-md border border-slate-300 px-3 text-base focus:border-teal-700 focus:outline-none focus:ring-1 focus:ring-teal-700";

let rowSeq = 0;
const newId = () => `r${Date.now()}-${rowSeq++}`;

interface FactRow {
  id: string;
  key: string;
  label: string;
  valueType: FactValueType;
  unit?: string;
  options?: string[];
  value: string;
  /** From a template (fixed key/label) vs a user-added custom row. */
  canonical: boolean;
}

interface DateRow {
  id: string;
  key: string;
  value: string;
  precision: DatePrecision;
  estimated: boolean;
}

function factsToRows(entry: BinderEntry | undefined): FactRow[] {
  if (!entry?.facts) return [];
  const templateKeys = new Set(findTemplate(entry.templateId)?.fields.map((f) => f.key) ?? []);
  return Object.entries(entry.facts).map(([key, f]) => ({
    id: newId(),
    key,
    label: f.label,
    valueType: f.valueType,
    unit: f.unit,
    value: String(f.value),
    canonical: templateKeys.has(key),
  }));
}

function datesToRows(entry: BinderEntry | undefined): DateRow[] {
  if (!entry?.dates) return [];
  return Object.entries(entry.dates).map(([key, d]) => ({
    id: newId(),
    key,
    value: d.value,
    precision: d.precision,
    estimated: d.estimated ?? false,
  }));
}

export function BinderEntryForm({
  propertyId,
  mode,
  initial,
}: {
  propertyId: string;
  mode: "create" | "edit";
  initial?: BinderEntry;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const createMutation = useCreateBinderEntry(propertyId);
  const updateMutation = useUpdateBinderEntry(propertyId, initial?.id ?? "");
  const deleteMedia = useDeleteBinderMedia(propertyId, initial?.id ?? "");
  // New photos held in memory; uploaded after the entry exists (Slice 3).
  const { images: draftPhotos, addFiles, removeImage } = useDraftImages();
  // Already-saved photos (edit mode). Tracked locally so removals reflect at once.
  const [existingMedia, setExistingMedia] = useState<BinderMedia[]>(initial?.media ?? []);
  const [submitting, setSubmitting] = useState(false);

  const [templateId, setTemplateId] = useState<BinderTemplateId | undefined>(initial?.templateId);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [locationLabel, setLocationLabel] = useState(initial?.locationLabel ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [facts, setFacts] = useState<FactRow[]>(() => factsToRows(initial));
  const [dates, setDates] = useState<DateRow[]>(() => datesToRows(initial));
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [tagDraft, setTagDraft] = useState("");
  // Blank entries hide the details area until asked; templated ones show it.
  const [detailsOpen, setDetailsOpen] = useState(
    Boolean(initial?.templateId) ||
      (initial?.facts && Object.keys(initial.facts).length > 0) ||
      (initial?.dates && Object.keys(initial.dates).length > 0) ||
      Boolean(initial?.locationLabel),
  );
  const [error, setError] = useState<string | null>(null);

  const isCreate = mode === "create";

  function onAddFiles(fileList: FileList | null) {
    const errs = addFiles(fileList);
    setError(errs.length > 0 ? errs.join("; ") : null);
  }

  function onRemoveExisting(mediaId: string) {
    setExistingMedia((prev) => prev.filter((m) => m.id !== mediaId));
    deleteMedia.mutate(mediaId);
  }

  function applyTemplate(id: BinderTemplateId | undefined) {
    setTemplateId(id);
    setDetailsOpen(true);
    const tmpl = findTemplate(id);
    if (!tmpl) {
      setFacts([]);
      setDates([]);
      return;
    }
    // Seed empty rows for the template's suggested fields + dates. Don't clobber
    // a title the user already typed.
    setTitle((t) => (t.trim().length === 0 ? tmpl.label : t));
    setFacts(
      tmpl.fields.map((f) => ({
        id: newId(),
        key: f.key,
        label: f.label,
        valueType: f.valueType,
        unit: f.unit,
        options: f.options,
        value: "",
        canonical: true,
      })),
    );
    setDates(
      tmpl.suggestedDateKeys.map((key) => ({
        id: newId(),
        key,
        value: "",
        precision: "year" as DatePrecision,
        estimated: false,
      })),
    );
  }

  function buildInput(): CreateBinderEntryInput {
    const factsRecord: Record<string, BinderFact> = {};
    for (const r of facts) {
      const raw = r.value.trim();
      if (!raw) continue;
      let value: string | number | boolean = raw;
      if (r.valueType === "number") {
        const n = Number(raw);
        if (!Number.isFinite(n)) continue;
        value = n;
      }
      factsRecord[r.key] = {
        label: r.label,
        valueType: r.valueType,
        value,
        ...(r.unit ? { unit: r.unit } : {}),
      };
    }

    const datesRecord: Record<string, BinderDateValue> = {};
    for (const d of dates) {
      const v = d.value.trim();
      if (!v) continue;
      datesRecord[d.key] = { value: v, precision: d.precision, estimated: d.estimated };
    }

    return {
      ...(isCreate && templateId ? { templateId } : {}),
      title: title.trim(),
      ...(locationLabel.trim() ? { locationLabel: locationLabel.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...(Object.keys(factsRecord).length > 0 ? { facts: factsRecord } : {}),
      ...(Object.keys(datesRecord).length > 0 ? { dates: datesRecord } : {}),
    };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (title.trim().length === 0) {
      setError("Give it a title.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const input = buildInput();
      let entryId: string;
      if (isCreate) {
        const created = await createMutation.mutateAsync(input);
        entryId = created.id;
      } else {
        // Update can't change templateId/entryType; send content fields only.
        const { templateId: _t, ...content } = input;
        void _t;
        const updated = await updateMutation.mutateAsync(content);
        entryId = updated.id;
      }
      // Upload any new photos now that the entry exists, then land on its detail.
      for (const photo of draftPhotos) {
        await uploadBinderMedia({
          propertyId,
          entryId,
          file: photo.file,
          contentType: photo.contentType,
        });
      }
      if (draftPhotos.length > 0) {
        void qc.invalidateQueries({ queryKey: ["properties", propertyId, "binder", entryId] });
      }
      navigate(`/app/properties/${propertyId}/binder/${entryId}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save this entry.");
      setSubmitting(false);
    }
  }

  const addTag = () => {
    const t = tagDraft.trim();
    if (t && !tags.includes(t) && tags.length < 20) setTags((prev) => [...prev, t]);
    setTagDraft("");
  };

  const heading = useMemo(
    () => (isCreate ? "New entry" : "Edit entry"),
    [isCreate],
  );

  return (
    <div>
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-teal-700"
        >
          ‹ Cancel
        </button>
        <h1 className="mt-1 text-xl font-semibold">{heading}</h1>
      </header>

      <form onSubmit={onSubmit} className="space-y-4 p-4" noValidate>
        {error && <div className="rounded bg-rose-50 p-3 text-sm text-rose-900">{error}</div>}

        <PhotosSection
          propertyId={propertyId}
          entryId={initial?.id}
          existing={existingMedia}
          drafts={draftPhotos}
          onAdd={onAddFiles}
          onRemoveDraft={removeImage}
          onRemoveExisting={onRemoveExisting}
        />

        {isCreate && (
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">Start from a template</p>
            <div className="flex flex-wrap gap-2">
              <TemplateChip active={!templateId} onClick={() => applyTemplate(undefined)}>
                Note
              </TemplateChip>
              {BINDER_TEMPLATES.map((t) => (
                <TemplateChip
                  key={t.id}
                  active={templateId === t.id}
                  onClick={() => applyTemplate(t.id)}
                >
                  {t.label}
                </TemplateChip>
              ))}
            </div>
          </div>
        )}

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Title</span>
          <input
            autoFocus
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
            placeholder="Basement water heater"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Note</span>
          <textarea
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass + " py-2"}
            placeholder="Anything useful to remember…"
          />
        </label>

        {!detailsOpen ? (
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            className="text-sm font-medium text-teal-700"
          >
            + Add details
          </button>
        ) : (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Location</span>
              <input
                value={locationLabel}
                maxLength={80}
                onChange={(e) => setLocationLabel(e.target.value)}
                className={inputClass}
                placeholder="Basement, Kitchen, Back yard…"
              />
            </label>

            <FactsEditor facts={facts} setFacts={setFacts} />

            <DatesEditor dates={dates} setDates={setDates} />

            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">Tags</span>
              {tags.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => setTags((prev) => prev.filter((x) => x !== t))}
                        aria-label={`Remove ${t}`}
                        className="text-slate-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  maxLength={40}
                  className={inputClass}
                  placeholder="Add a tag"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="min-h-[44px] shrink-0 rounded-md border border-slate-300 px-4 text-sm font-medium text-slate-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || title.trim().length === 0}
          className="min-h-[44px] w-full rounded-md bg-teal-700 text-sm font-medium text-white disabled:opacity-60"
        >
          {submitting ? "Saving…" : "Save entry"}
        </button>
      </form>
    </div>
  );
}

function PhotosSection({
  propertyId,
  entryId,
  existing,
  drafts,
  onAdd,
  onRemoveDraft,
  onRemoveExisting,
}: {
  propertyId: string;
  entryId: string | undefined;
  existing: BinderMedia[];
  drafts: DraftImage[];
  onAdd: (files: FileList | null) => void;
  onRemoveDraft: (localId: string) => void;
  onRemoveExisting: (mediaId: string) => void;
}) {
  const total = existing.length + drafts.length;
  const canAdd = total < 20;
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-slate-700">Photos</span>
      {total > 0 && (
        <ul className="mb-2 grid grid-cols-3 gap-2">
          {entryId &&
            existing.map((m) => (
              <li key={m.id}>
                <BinderMediaThumb
                  propertyId={propertyId}
                  entryId={entryId}
                  mediaId={m.id}
                  onRemove={() => onRemoveExisting(m.id)}
                />
              </li>
            ))}
          {drafts.map((d) => (
            <li key={d.localId} className="relative">
              <img
                src={d.previewUrl}
                alt=""
                className="aspect-square w-full rounded-md object-cover"
              />
              <button
                type="button"
                onClick={() => onRemoveDraft(d.localId)}
                aria-label="Remove photo"
                className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {canAdd && (
        <label className="flex min-h-[44px] cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white px-4 text-sm font-medium text-teal-700">
          {total === 0 ? "Take or choose photos" : "Add more"}
          <input
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              onAdd(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      )}
    </div>
  );
}

function TemplateChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
        active ? "border-teal-700 bg-teal-50 text-teal-800" : "border-slate-300 bg-white text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function FactsEditor({
  facts,
  setFacts,
}: {
  facts: FactRow[];
  setFacts: React.Dispatch<React.SetStateAction<FactRow[]>>;
}) {
  const setValue = (id: string, value: string) =>
    setFacts((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));
  const setLabel = (id: string, label: string) =>
    setFacts((prev) => prev.map((f) => (f.id === id ? { ...f, label } : f)));
  const remove = (id: string) => setFacts((prev) => prev.filter((f) => f.id !== id));
  const addCustom = () =>
    setFacts((prev) => [
      ...prev,
      { id: newId(), key: `custom_${newId()}`, label: "", valueType: "text", value: "", canonical: false },
    ]);

  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-slate-700">Details</span>
      <div className="space-y-2">
        {facts.map((f) => (
          <div key={f.id} className="flex items-center gap-2">
            {f.canonical ? (
              <span className="w-28 shrink-0 text-sm text-slate-600">{f.label}</span>
            ) : (
              <input
                value={f.label}
                onChange={(e) => setLabel(f.id, e.target.value)}
                maxLength={80}
                placeholder="Label"
                className={inputClass + " w-28 shrink-0"}
              />
            )}
            {f.valueType === "select" && f.options ? (
              <select
                value={f.value}
                onChange={(e) => setValue(f.id, e.target.value)}
                className={inputClass + " flex-1"}
              >
                <option value="">Select…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={f.value}
                onChange={(e) => setValue(f.id, e.target.value)}
                inputMode={f.valueType === "number" ? "decimal" : undefined}
                maxLength={500}
                className={inputClass + " flex-1"}
              />
            )}
            {f.unit && <span className="w-10 shrink-0 text-sm text-slate-500">{f.unit}</span>}
            <button
              type="button"
              onClick={() => remove(f.id)}
              aria-label="Remove detail"
              className="shrink-0 text-slate-400"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addCustom}
        className="mt-2 text-sm font-medium text-teal-700"
      >
        + Add detail
      </button>
    </div>
  );
}

function DatesEditor({
  dates,
  setDates,
}: {
  dates: DateRow[];
  setDates: React.Dispatch<React.SetStateAction<DateRow[]>>;
}) {
  const usedKeys = new Set(dates.map((d) => d.key));
  const available = BINDER_DATE_KINDS.filter((k) => !usedKeys.has(k.key));
  const set = (id: string, patch: Partial<DateRow>) =>
    setDates((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const remove = (id: string) => setDates((prev) => prev.filter((d) => d.id !== id));

  const placeholderFor = (p: DatePrecision) =>
    p === "year" ? "2018" : p === "month" ? "2018-03" : "2018-03-14";

  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-slate-700">Dates</span>
      <div className="space-y-2">
        {dates.map((d) => (
          <div key={d.id} className="rounded-md border border-slate-200 p-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{dateKindLabel(d.key)}</span>
              <button
                type="button"
                onClick={() => remove(d.id)}
                aria-label="Remove date"
                className="text-slate-400"
              >
                ×
              </button>
            </div>
            <div className="mt-1.5 flex gap-2">
              <input
                value={d.value}
                onChange={(e) => set(d.id, { value: e.target.value })}
                inputMode="numeric"
                placeholder={placeholderFor(d.precision)}
                className={inputClass + " flex-1"}
              />
              <select
                value={d.precision}
                onChange={(e) => set(d.id, { precision: e.target.value as DatePrecision })}
                className={inputClass + " w-28 shrink-0"}
              >
                <option value="year">Year</option>
                <option value="month">Month</option>
                <option value="day">Day</option>
              </select>
            </div>
            <label className="mt-1.5 flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={d.estimated}
                onChange={(e) => set(d.id, { estimated: e.target.checked })}
              />
              Approximate
            </label>
          </div>
        ))}
      </div>
      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => {
            const key = e.target.value;
            if (!key) return;
            setDates((prev) => [
              ...prev,
              { id: newId(), key, value: "", precision: "year", estimated: false },
            ]);
          }}
          className={inputClass + " mt-2"}
        >
          <option value="">+ Add a date…</option>
          {available.map((k) => (
            <option key={k.key} value={k.key}>
              {k.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
